import { useAppState, useBrandId } from 'AppProvider';
import { checkoutDiagnostics } from 'features/analytics';
import useCartItems from 'hooks/useCartItems';
import { useEffect, useRef } from 'react';
import {
  CART_LS_KEY,
  ICartExperience,
  ICartState,
  ICartStorageData,
} from 'types/cart';
import { getLSItem } from 'utils/localStorage';

const countItemStates = (items: Record<string, ICartExperience>) =>
  Object.values(items).reduce<Record<string, number>>(
    (counts, item) => ({
      ...counts,
      [item.state]: (counts[item.state] ?? 0) + 1,
    }),
    {},
  );

/**
 * Records checkout mounts and reports each time checkout falls to the
 * empty-cart screen, with what the cart held at that moment, so an unexpected
 * "nothing in your cart" in production can be traced to its cause.
 */
const useCheckoutDiagnostics = (isCartEmpty: boolean) => {
  const brandId = useBrandId();
  const { actualCartItems, cartId, paymentIntentRevision } = useCartItems();
  const isLastChangeRestored = useAppState(
    (state: { cart: ICartState }) => !!state.cart.isLastChangeRestored,
  );
  const hasReportedEmptyCartRef = useRef(false);

  useEffect(() => {
    checkoutDiagnostics.recordCheckoutMount();
  }, []);

  useEffect(() => {
    if (!isCartEmpty) {
      hasReportedEmptyCartRef.current = false;
      return;
    }
    if (hasReportedEmptyCartRef.current) return;
    hasReportedEmptyCartRef.current = true;

    const persistedCart = (getLSItem(CART_LS_KEY) as ICartStorageData | null)?.[
      brandId
    ];

    checkoutDiagnostics.reportEmptyCart({
      cart: { cartId, isLastChangeRestored, paymentIntentRevision },
      itemStateCounts: countItemStates(actualCartItems),
      persistedItemCount: Object.keys(persistedCart?.cartItems ?? {}).length,
      persistedItemStateCounts: countItemStates(persistedCart?.cartItems ?? {}),
    });
  }, [
    actualCartItems,
    brandId,
    cartId,
    isCartEmpty,
    isLastChangeRestored,
    paymentIntentRevision,
  ]);
};

export default useCheckoutDiagnostics;
